import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { viewport, wipeDown, SNAP } from "../../utils/motion";

// The dell-1996 "ribbon card", the system's signature container: a title bar
// carrying an all-caps label with a 1px rule beneath it, then a body block.
// Sharp corners, hard 1px border, no shadow — see DESIGN.md:513-531.
//
// On scroll the card "opens": the title bar snaps in first, then the body
// unrolls downward from underneath it. Pass reveal={false} when a parent
// stagger already drives the entrance.
//
// Three square markers sit at the right of the title bar. In the objective
// these read as window controls; here they are decorative chrome only, so they
// are aria-hidden.

const Frame = styled(motion.div)`
  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: ${({ $tone, theme }) =>
    (theme.biome[$tone] || theme.biome.about).card};
  border: 1px solid ${({ $line }) => $line};
  border-radius: 0;
  overflow: hidden;
`;

const Bar = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  gap: 12px;
  padding: 7px 10px;
  background: ${({ $barBg }) => $barBg};
  border-bottom: 1px solid ${({ $line }) => $line};
`;

const Label = styled.span`
  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${({ $barInk }) => $barInk};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Markers = styled.span`
  display: inline-flex;
  gap: 4px;
  flex: none;
`;

// Squares, not dots. Round window controls belong to a different design era.
const Marker = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 0;
  background: ${({ $on, theme, $barInk }) => ($on ? theme.orange : $barInk)};
  opacity: ${({ $on }) => ($on ? 1 : 0.4)};
`;

// Grows to fill the frame so sibling windows in a stretched grid row share a
// baseline. `position: relative` lets a child pin itself to the body box — the
// About portrait uses this to crop to the row height instead of setting it.
const Body = styled(motion.div)`
  position: relative;
  flex: 1;
  padding: ${({ $pad }) => $pad};
`;

// Title bar snaps in from above; the body then unrolls from underneath it.
const barVariants = {
  hidden: { opacity: 0, y: -10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.32, ease: SNAP } },
};

const frameVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

/**
 * @param label     title-bar text (rendered uppercase)
 * @param tone      biome key — about | experience | projects | connect | hero | footer
 * @param bare      omit the title bar entirely
 * @param pad       body padding override
 * @param reveal    self-animate on scroll; set false when a parent drives it
 */
const Window = ({
  label,
  tone = "about",
  bare = false,
  pad = "16px",
  reveal = true,
  children,
  ...rest
}) => {
  // Light biomes need dark chrome, dark biomes need light chrome.
  const isLight = tone === "experience" || tone === "connect";
  const line = isLight ? "rgba(0,0,0,0.28)" : "rgba(255,255,255,0.16)";
  const barBg = isLight ? "#3a2a1a" : "#000000";
  const barInk = isLight ? "#f8eee1" : "#ffffff";

  // When `reveal` is off the parent's stagger supplies hidden/show, so the
  // children still animate — they just inherit the trigger.
  const motionProps = reveal
    ? { initial: "hidden", whileInView: "show", viewport }
    : {};

  return (
    <Frame $line={line} $tone={tone} variants={frameVariants} {...motionProps} {...rest}>
      {!bare && (
        <Bar $barBg={barBg} $line={line} variants={barVariants}>
          <Label $barInk={barInk}>{label}</Label>
          <Markers aria-hidden="true">
            <Marker $on $barInk={barInk} />
            <Marker $barInk={barInk} />
            <Marker $barInk={barInk} />
          </Markers>
        </Bar>
      )}
      <Body $pad={pad} variants={wipeDown}>
        {children}
      </Body>
    </Frame>
  );
};

export default Window;
